import { useEffect, useState, type ReactNode } from 'react';

import { listTags } from '@/api/tags';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { useAuth } from './useAuth';

type GateState =
  | { phase: 'unknown' }
  | { phase: 'validating' }
  | { phase: 'invalid'; reason: string }
  | { phase: 'ok' };

/**
 * Outermost wrapper. Validates the stored token by hitting GET /api/tags
 * (auth-required, small response). Renders children only when a valid token
 * is present.
 */
export function TokenGate({ children }: { children: ReactNode }) {
  const { token, signIn, signOut } = useAuth();
  const [gate, setGate] = useState<GateState>({ phase: 'unknown' });
  const [draft, setDraft] = useState('');

  useEffect(() => {
    if (!token) {
      setGate({ phase: 'unknown' });
      return;
    }
    let cancelled = false;
    setGate({ phase: 'validating' });
    listTags()
      .then(() => {
        if (!cancelled) setGate({ phase: 'ok' });
      })
      .catch((err: { status?: number }) => {
        if (cancelled) return;
        if (err.status === 401) {
          signOut();
          setGate({ phase: 'invalid', reason: 'Token rejected by server' });
        } else {
          setGate({ phase: 'invalid', reason: 'Could not reach the API' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token, signOut]);

  if (gate.phase === 'ok') return <>{children}</>;

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (draft.trim()) signIn(draft.trim());
  };

  return (
    <Dialog open>
      <DialogContent
        className="sm:max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>HyperBookmarks</DialogTitle>
          <DialogDescription>
            Paste your access token to continue. Tokens are configured on the server in
            <code className="mx-1 rounded bg-muted px-1 py-0.5 text-xs">data/users.json</code>.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="token-input">Token</Label>
            <Input
              id="token-input"
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="paste your token here"
            />
            {gate.phase === 'invalid' && <p className="text-sm text-destructive">{gate.reason}</p>}
            {gate.phase === 'validating' && (
              <p className="text-sm text-muted-foreground">Validating…</p>
            )}
          </div>
          <Button type="submit" disabled={gate.phase === 'validating'}>
            Save & Continue
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
